import { createApi } from "@reduxjs/toolkit/query/react";
import { axiosBaseQuery } from "./axiosBaseQuery";
import creditApi from "./creditApi";
import type { SavedContactsFilterDict } from "@/lib/utils/people-search-utils/saved-contacts-filter.utils";
import { normalizeSelectionIds } from "@/lib/utils/selection-ids.utils";

/**
 * Saved Contacts API.
 *
 * Talks to the real Express middleware at `/api/saved-contacts/*`. The
 * middleware resolves the user from the session cookie and forwards to the
 * Saved Contacts service, so no user id is sent from the browser. A
 * single-id save doubles as "unlock" for that contact.
 */

interface Envelope<T> {
  success: boolean;
  message?: string;
  data: T;
}

/**
 * One saved-contact hit as the upstream service actually returns it (confirmed live via
 * /api/v1/contacts/saved/search) - the same snake_case shape as the live contact search API
 * (see contacts-data.utils.ts's processCurrentContacts), not a bespoke "saved" schema.
 * `person_id` is the only stable identifier. `added_at`/`modified_at` carry when the save
 * happened; `source` carries where the contact was sourced from (e.g. "infynd").
 */
export interface SavedContactRecord {
  person_id?: string | number;
  id?: string;
  first_name?: string;
  last_name?: string;
  full_name?: string;
  position?: string;
  designation?: string;
  primary_email?: string;
  phoneNumber?: { mobile?: string[] };
  mobile?: string;
  phone?: string;
  person_city?: string;
  person_state?: string;
  person_country?: string;
  company_name?: string;
  industry?: string | string[];
  // UK-specific classification carried by uk_data companies - the Industry
  // fallback when `industry` is empty.
  uk_industry?: string;
  comp_size_range?: string;
  city?: string;
  state?: string;
  country?: string;
  company_id?: string;
  // Company website - the fallback domain for the masked-email display when a locked
  // contact carries no primary_email to derive one from (see EmailUnlockCell).
  website?: string;
  dateAdded?: string;
  added_at?: string;
  modified_at?: string;
  source?: string;
  // Unlock status flags from API - controls whether email/phone are displayed
  email_unlocked?: boolean;
  phone_unlocked?: boolean;
  // Export state from API - set by saved.Service.MarkExported on export.
  // `exported_date` arrives as Go zero time ("0001-01-01T00:00:00Z") when never
  // exported (omitempty doesn't drop a zero time.Time struct), so callers must
  // gate on `exported === true` before formatting it - formatDate would
  // otherwise render it as "01 Jan 0001".
  exported?: boolean;
  exported_date?: string;
  // Phone numbers from API - maps to contactPhone in Contact
  direct_dials?: string[];
  // Per-provider outcome, keyed by provider - confirmed real shape:
  // `{ email, email_verification: "verified" | "not_verified_guessed", status: "found", verification: boolean }`.
  // Durable equivalent of the live workflow's own result, so a contact
  // unlocked in an earlier session still shows its actual verification and
  // provider history (see EmailUnlockCell / EmailProviderModal) rather than
  // just the bare primary_email with no context.
  emailProviders?: Record<string, { email?: string; email_verification?: string; status?: string; verification?: boolean }>;
  // The phone counterpart of emailProviders above. No verification checkpoint
  // exists for phone (see phone-workflow.types.ts), so an entry carries only
  // the number(s) a provider returned and whether it found anything - the
  // number itself may arrive as `directdials[]`, `phone_number.mobile[]` or a
  // plain `phone`, so PhoneUnlockCell normalises across those rather than
  // assuming one.
  phoneProviders?: Record<string, Record<string, unknown>>;
  [key: string]: unknown;
}

export interface SavedContactsSearchResponse {
  total: number;
  results: SavedContactRecord[];
}

export const savedContactsApi = createApi({
  reducerPath: "savedContactsApi",
  baseQuery: axiosBaseQuery("/api/saved-contacts"),
  tagTypes: ["SavedContacts"],
  endpoints: (builder) => ({
    saveContacts: builder.mutation<
      unknown,
      { contactIds: string[]; unlockType?: "email" | "phone" | "both" }
    >({
      query: ({ contactIds, unlockType }) => ({
        url: "/save",
        method: "POST",
        body: unlockType ? { contactIds, unlockType } : { contactIds },
      }),
      invalidatesTags: ["SavedContacts"],
      /**
       * A save IS an unlock, so the Saved Contacts service debits for it. The
       * balance lives in a separate createApi instance, so nothing here
       * invalidates it on its own — without this the top-bar badge keeps
       * showing the pre-unlock figure until its cache lapses (60s) or the page
       * reloads, which reads as "the unlock was free".
       *
       * Waits for the mutation to settle and stays quiet if it failed: nothing
       * was charged in that case.
       */
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
          dispatch(creditApi.util.invalidateTags(["CreditBalance", "CreditHistory"]));
        } catch {
          // Save failed — no debit, nothing to refresh.
        }
      },
    }),

    searchSavedContacts: builder.query<
      SavedContactsSearchResponse,
      (SavedContactsFilterDict & { from?: number; size?: number }) | void
    >({
      query: (args) => {
        const { from = 0, size = 10, ...filterDict } = args ?? {};
        return {
          url: "/search",
          method: "POST",
          // filterDict only ever has keys buildSavedContactsFilterDict actually set (it never
          // assigns an empty array/falsy value), so this is already exactly what the Saved
          // Contacts service's SavedFilter struct expects - no further shaping needed here.
          body: filterDict,
          params: { from, size },
        };
      },
      transformResponse: (response: Envelope<any>) => {
        const data = response?.data ?? {};
        const results = data.results ?? data.contacts ?? data.savedContacts ?? [];
        return {
          total: typeof data.total === "number" ? data.total : results.length,
          results: Array.isArray(results) ? results : [],
        };
      },
      providesTags: ["SavedContacts"],
    }),

    /**
     * Ids-only counterpart of searchSavedContacts, for the bulk action bar's
     * "Select N". Same filter body and paging, but the upstream projects only
     * the key field and answers { total, from, size, ids } - so selecting N
     * rows costs N person_id strings rather than N full saved snapshots.
     *
     * This is what lets the Saved tab select past the slice it holds in
     * memory: selection used to be served entirely from `allSavedContacts`,
     * which is fetched capped at 1000 rows, so "Select 5000" could never
     * pick more than 1000 no matter how many were saved.
     */
    searchSavedContactsSelection: builder.query<
      string[],
      (SavedContactsFilterDict & { from?: number; size?: number }) | void
    >({
      query: (args) => {
        const { from = 0, size = 25, ...filterDict } = args ?? {};
        return {
          url: "/search/selection",
          method: "POST",
          body: filterDict,
          params: { from, size },
        };
      },
      transformResponse: (response: Envelope<unknown>) => normalizeSelectionIds(response),
      // Selection data is temporary - and deliberately NOT tagged
      // "SavedContacts": a save/delete should refresh the visible list, not
      // silently re-run an arbitrary "Select 5000" behind the user.
      keepUnusedDataFor: 60,
    }),

    deleteSavedContacts: builder.mutation<{ deleted: number }, { contactIds: string[] }>({
      query: ({ contactIds }) => ({
        url: "/",
        method: "DELETE",
        body: { contactIds },
      }),
      transformResponse: (response: Envelope<{ deleted: number }>) => ({
        deleted: response?.data?.deleted ?? 0,
      }),
      invalidatesTags: ["SavedContacts"],
      /**
       * Drops the deleted rows from every cached saved list as soon as the
       * delete succeeds. The invalidation above still refetches, but that
       * refetch pulls up to 1000 full records, and until it lands the table
       * would keep showing the rows that were just deleted.
       */
      async onQueryStarted({ contactIds }, { dispatch, getState, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch {
          return; // Delete failed - the rows are still saved.
        }
        const deletedIds = new Set(contactIds);
        const cachedArgs = savedContactsApi.util.selectCachedArgsForQuery(getState(), "searchSavedContacts");
        cachedArgs.forEach((args) => {
          dispatch(
            savedContactsApi.util.updateQueryData("searchSavedContacts", args, (draft) => {
              const before = draft.results.length;
              draft.results = draft.results.filter(
                (record) => !deletedIds.has(String(record.person_id ?? record.id ?? ""))
              );
              draft.total = Math.max(0, draft.total - (before - draft.results.length));
            })
          );
        });
      },
    }),

    exportSavedContacts: builder.mutation<
      unknown,
      {
        firstname: string;
        lastname: string;
        primary_email: string;
        cntid_list: string[];
        pageFlag: string;
      }
    >({
      query: (payload) => ({
        url: "/export",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: Envelope<unknown>) => response?.data ?? response,
    }),

    /**
     * All-tab export: unlocks the given live-search contacts (they need not be
     * saved) and emails the CSV once unlocking settles. Async on the Search
     * Service - this only confirms the job started.
     */
    exportContactsWithUnlock: builder.mutation<
      { jobId?: string; status?: string },
      {
        firstname: string;
        lastname: string;
        primary_email: string;
        unlock: "email" | "phone" | "both";
        items: { personId: string; companyId: string }[];
      }
    >({
      query: (payload) => ({
        url: "/export-with-unlock",
        method: "POST",
        body: payload,
      }),
      transformResponse: (response: Envelope<{ jobId?: string; status?: string }>) =>
        response?.data ?? {},
    }),

    /**
     * Queues the selected saved contacts for a push into the connected CRM.
     * The push runs in the background on the CRM service, so this only
     * confirms it started.
     */
    exportSavedContactsToCrm: builder.mutation<
      { batchId?: string; status?: string },
      {
        crmType: "hubspot" | "salesforce";
        contactIds: string[];
        /** HubSpot list id / Salesforce Campaign id; omit for none. */
        segmentId?: string;
      }
    >({
      query: (body) => ({
        url: "/export-crm",
        method: "POST",
        body,
      }),
      transformResponse: (response: Envelope<{ batchId?: string; status?: string }>) => response.data,
    }),
  }),
});

export const {
  useSaveContactsMutation,
  useSearchSavedContactsQuery,
  useLazySearchSavedContactsQuery,
  useLazySearchSavedContactsSelectionQuery,
  useDeleteSavedContactsMutation,
  useExportSavedContactsMutation,
  useExportSavedContactsToCrmMutation,
  useExportContactsWithUnlockMutation,
} = savedContactsApi;

export default savedContactsApi;
